/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'gemini/lib/@waca/dashboard-common/dist/core/APIFactory',
	'./SmartTitleAPI'
], function (APIFactory, SmartTitleAPI) {
	class SmartTitleFeatureProvider {

		constructor(options) {
			this._dashboard = options.dashboardAPI;
			this._content = options.content;
			this._dashboardInternal = options.features['Dashboard.internal'];
			this._widgetApiHandlers = {};

			//tracking event to detect if this widget was added after dashboard open
			//event listener will be removed on render state change
			this._addedContent = [];
			const canvas = this._dashboard.getCanvas();
			if( canvas ) {
				this._addContentEvent = canvas.on('add:content:child', (event) => {
					this._addedContent.push( event.info.newContentId);
				});
			}

			this._state = {
				setupDone: false,
				setupCheckDone: false,
				initialWidgetRenderDone: false,
				shouldUseGeneratedTitle: undefined,
				smartTitleEnabled: undefined,
				toEnableGeneratedTitle: undefined,
				toSetNoTitle: undefined,
				toSetSmartTitle: undefined
			};
		}

		_isSmartTitleFeatureEnabled() {
			return !this._dashboard.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'SmartTitle', 'disabled');
		}

		_isSmartTitleEnabled() {
			return this._isSmartTitleFeatureEnabled() && this._state.smartTitleEnabled;
		}

		getAPI() {
			return APIFactory.createAPI(this, [SmartTitleAPI]); //each content object has its own 'feature' impl
		}

		initialize() {
			this.prepare();
		}

		prepare() {
			if (!this._isSmartTitleFeatureEnabled()) {
				return;
			}
			this.upgradeIfNeeded();
			this._setupTitle();
			this._addChangeHandlerForTitleModeProperty();
		}

		_getModelAttr(name) {
			const boardModel = this._dashboardInternal.getBoardModel();
			const id = this._content.getId();
			const widgetModel = boardModel && boardModel.getWidgetModel(id);
			if(widgetModel) {
				return widgetModel.get(name);
			}
			return undefined;
		}

		_setModelAttr(attribs, options = { silent: true }) {
			const boardModel = this._dashboardInternal.getBoardModel();
			const id = this._content.getId();
			const widgetModel = boardModel && boardModel.getWidgetModel(id);
			widgetModel && widgetModel.set(attribs, options);
		}

		_setProp(name, value) {
			if (this._state.initialWidgetRenderDone) {
				this._content.setPropertyValue(name, value);
			} else {
				let stateAPI = this._content.getFeature('state');
				stateAPI.whenStatusChanges('rendered').then(() => {
					//Content api does not allow SKIP undo/red0 event yet. Need to set in the model and skip the event for the first time
					//this._content.setPropertyValue(name, value);

					const boardModel = this._dashboardInternal.getBoardModel();
					const id = this._content.getId();
					const widgetModel = boardModel && boardModel.getWidgetModel(id);
					if( widgetModel ) {
						const attribs = {};
						attribs[name] = value;
						widgetModel.set(attribs, { payloadData: { skipUndoRedo: true } });
					}
				});
			}
		}

		upgradeIfNeeded() {
			//TODO: feature manages its own upgrade, but could we allow others to customize upgrade process ?
			const container = this._content.getContainer();
			const containerType = container && container.getType();
			const forceSmartTitle = (containerType && containerType === 'exploreCard');

			if(forceSmartTitle) {
				let attribs = {
					showTitle: undefined,
					titleMode : 'smartTitle'
				};
				this._setModelAttr(attribs);
				this._setProp('titleMode', 'smartTitle');
				this._setProp('showTitle', undefined);
				return;
			}

			let showTitle = this._getModelAttr('showTitle');
			const name = this._getModelAttr('name');

			const titleMode = this._getModelAttr('titleMode');

			if (showTitle !== undefined) {
				let attribs = {
					showTitle: undefined
				};
				if (name && (!titleMode || titleMode !== 'noTitle') ) {
					if( showTitle === true ) {
						attribs.titleMode = 'customTitle';
						this._setProp('titleMode', 'customTitle');
					} else {
						attribs.titleMode = 'noTitle';
						this._setProp('titleMode', 'noTitle');
					}
				}
				this._setModelAttr(attribs);
				this._setProp('showTitle', undefined);
			}
		}


		_setupTitle() {
			//if no title set & showTitle prop is true and generatedTitle is undefined, enable smart title
			//if showTitle is true & generatedTitle true, enable smart title (does not matter if title is set or not)
			const stateAPI = this._content.getFeature('state');
			stateAPI.whenStatusChanges('rendered').then(() => {
				this._state.initialWidgetRenderDone = true;
			});

			this._stateInitPromise = stateAPI.whenStatusChanges('rendering');
			this._stateInitPromise.then(() => {
				this._ensureStateCheckOnInit();
				this._state.setupCheckDone = true;

				if (!this._state.setupDone) {
					this._state.setupDone = true;
					if (this._state.toEnableGeneratedTitle && this._state.toSetSmartTitle) {
						this.enableAndSetSmartTitle();
					} else if (this._state.toEnableGeneratedTitle) {
						this._enableSmartTitle();
					} else if (this._state.toSetNoTitle) {
						this._setProp('titleMode', 'noTitle');
					}
				}
			});
		}

		_removeAddContentEventListener() {
			if( this._addContentEvent && this._addContentEvent.remove ) {
				this._addContentEvent.remove();
				this._addContentEvent = null;
			}
		}

		_ensureStateCheckOnInit() {

			this._removeAddContentEventListener();

			const id = this._content.getId();

			const isWidgetLoadedAfterDashboardOpen = this._addedContent.includes(id);


			const name = this._getModelAttr('name');

			const titleMode = this._content.getPropertyValue('titleMode');

			let shouldUseGeneratedTitle = false;
			if (name) {
				if (titleMode === 'smartTitle' || (!titleMode && isWidgetLoadedAfterDashboardOpen) )  {
					this._state.toEnableGeneratedTitle = true;
					shouldUseGeneratedTitle = true;
				} else if (titleMode !== 'customTitle' ){
					this._setProp('titleMode', 'noTitle');
				}
			} else {

				if (titleMode === undefined ) {
					if ( name !== undefined && name !== null && name === '' && !isWidgetLoadedAfterDashboardOpen) {
						this._state.toSetNoTitle = true;
					} else {
						//newly added widget
						this._state.toEnableGeneratedTitle = true;
						this._state.toSetSmartTitle = true;
						shouldUseGeneratedTitle = true;
					}
				} else if ( titleMode === 'smartTitle') {
					this._state.toEnableGeneratedTitle = true;
					this._state.toSetSmartTitle = true;
					shouldUseGeneratedTitle = true;
				}
			}

			this._state.shouldUseGeneratedTitle = shouldUseGeneratedTitle;
			return this._state;
		}

		shouldUseGeneratedTitle() {
			const smartTitleEnabledConfig = this._dashboard.getAppConfig('smartTitle');
			if (smartTitleEnabledConfig) {
				const state = this._ensureStateCheckOnInit();
				return state.shouldUseGeneratedTitle;
			}
			return false;
		}

		_addChangeHandlerForTitleModeProperty() {
			this._content.on('change:property:titleMode', (event) => {
				if (event.info.value === 'smartTitle') {
					this.enableAndSetSmartTitle();
				} else {
					this.disableSmartTitle(event.info.value);
				}
			});
		}

		enableAndSetSmartTitle() {
			if (this._isSmartTitleEnabled()) {
				return;
			}
			let widgetAPI = this._content.getFeature('WidgetAPI.deprecated');
			this._enableSmartTitle();
			this._triggerTitleUpdate(widgetAPI, true);
		}

		_enableSmartTitle() {
			if (this._isSmartTitleEnabled()) {
				return;
			}
			this._state.smartTitleEnabled = true;

			let widgetAPI = this._content.getFeature('WidgetAPI.deprecated');
			let visAPI = widgetAPI.getVisApi();

			this._deregisterListener();
			this._widgetApiHandlers = {
				_onChangeDefinition: visAPI && visAPI.on('change:definition', this._triggerTitleUpdate.bind(this, widgetAPI))
			};
			this._setProp('titleMode', 'smartTitle');
		}

		disableSmartTitle(newTitleMode = 'customTitle') {
			if (this._isSmartTitleEnabled() === false) {
				return;
			}
			this._state.smartTitleEnabled = false;
			this._deregisterListener();
			this._setProp('titleMode', newTitleMode);
		}

		getTitleState() {
			const name = this._getModelAttr('name');
			return {
				generatedTitle: this._isSmartTitleEnabled(),
				title: name
			};
		}


		shouldSetTitle( newTitle ) {
			const name = this._getModelAttr('name');
			if( name != newTitle ) {
				return true;
			}

			if(this._isTitlePartiallyFormatted(name)) {
				return true;
			}

			return false;
		}

		_isTitlePartiallyFormatted(name) {
			const unformattedPortionKey = name + '</span>';
			const titleHtml = this._getModelAttr('titleHtml');
			//if titleHtml has the full title unformatted ie does not have partial text formatted, we can set the title
			let currentTitleHtml = titleHtml.replace('</i>', '');
			currentTitleHtml = currentTitleHtml.replace('</u>', '');
			currentTitleHtml = currentTitleHtml.replace('</b>', '');
			if( currentTitleHtml.indexOf( unformattedPortionKey ) === -1 ) {
				return true;
			}
			return false;
		}


		getTitleHtmlWithPreviousFormat( title ) {

			const name = this._getModelAttr('name');
			if( name ) {
				if( this._isTitlePartiallyFormatted( name )) {
					return null;
				}

				const titleHtml = this._getModelAttr('titleHtml');
				if( titleHtml ) {
					const escapedName = name.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
					const regx = new RegExp(escapedName, 'g');
					const newTitleHtml = titleHtml.replace(regx, title);
					return newTitleHtml;
				}
			}
			return null;
		}


		destroy() {
			this._deregisterListener();
			return Promise.resolve(true);
		}

		_deregisterListener() {
			if (this._widgetApiHandlers._onChangeDefinition) {
				this._widgetApiHandlers._onChangeDefinition.remove();
				this._widgetApiHandlers._onChangeDefinition = null;
			}

			this._removeAddContentEventListener();
		}


		_triggerTitleUpdate(widgetAPI) {
			if (widgetAPI) {
				widgetAPI.updateTitle();
			}
		}

	}

	return SmartTitleFeatureProvider;

});
